import { useTranslate } from '@refinedev/core';
import { Flex, Space, Tag, Tooltip, Typography, theme } from 'antd';
import {
  CustomerServiceOutlined,
  EditOutlined,
  ReadOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import type { DateRange, PublicationListOut } from '@/api/generated/schemas';
import { StatusDot } from '@/components/common/StatusDot';
import { statusTone } from '@/components/common/statusTone';
import { ExclusivityTag } from './ExclusivityTag';
import { PublicationCoverThumb } from './PublicationCoverThumb';
import { personLine } from './personLine';

const { Text } = Typography;

/** Square side. `PublicationCoverThumb` keeps tall ebook art whole inside it. */
const COVER_SIZE = 48;
/** Caps the text block so a long title wraps instead of stretching a pinned
 * (`fixed`) column in a `max-content` table. */
const TEXT_WIDTH = 260;

/**
 * The fields the cell reads — a structural slice, so every list projection
 * that carries them (publications, premieres) can feed it.
 */
export type PublicationListCellRecord = Pick<
  PublicationListOut,
  | 'title'
  | 'cover_url'
  | 'ebook'
  | 'audio'
  | 'publication_authors'
  | 'publication_lectors'
  | 'is_exclusive'
  | 'is_premiere'
> & {
  /** Absent on projections without the window; the tag then shows on
   * `is_exclusive` alone, without the date tooltip. */
  exclusive_ranges?: DateRange | null;
};

interface PublicationListCellProps {
  record: PublicationListCellRecord;
  /** Off where the list states `is_premiere` in a column of its own. */
  showPremiereTag?: boolean;
}

/**
 * Publication list cell: cover with format icons, title, authors, lectors and
 * the exclusivity / premiere tags.
 */
export const PublicationListCell = ({
  record,
  showPremiereTag = true,
}: PublicationListCellProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const authors = personLine(
    record.publication_authors?.map((a) => ({
      position: a.position,
      name: a.author?.name,
    }))
  );
  const lectors = personLine(
    record.publication_lectors?.map((l) => ({
      position: l.position,
      name: l.lector?.name,
    }))
  );

  // Each format carries its own moderation status, independent of the
  // publication's.
  const formats = [
    record.ebook && {
      kind: 'ebook' as const,
      icon: <ReadOutlined />,
      modStatus: record.ebook.mod_status,
    },
    record.audio && {
      kind: 'audio' as const,
      icon: <CustomerServiceOutlined />,
      modStatus: record.audio.mod_status,
    },
  ].filter((format) => !!format);

  return (
    <Flex gap={12} align="flex-start">
      <div style={{ width: COVER_SIZE, flex: 'none' }}>
        <PublicationCoverThumb url={record.cover_url} size={COVER_SIZE} />
        {formats.length > 0 && (
          // Icon + moderation dot per format, as in `PublicationInfoPopover`.
          // Two pairs run a few px wider than the cover, so the row centres
          // on it by transform instead of being squeezed into its width.
          <Space
            size={6}
            style={{
              marginTop: 4,
              width: 'max-content',
              position: 'relative',
              left: '50%',
              transform: 'translateX(-50%)',
              color: token.colorTextSecondary,
            }}
          >
            {formats.map(({ kind, icon, modStatus }) => (
              <Space key={kind} size={2}>
                <Tooltip title={t(`user_catalog.publications.tabs.${kind}`)}>
                  {icon}
                </Tooltip>
                <StatusDot
                  tone={statusTone(modStatus)}
                  title={`${t(`user_catalog.publications.tabs.${kind}`)}: ${t(
                    `base.mod_status.${modStatus}`
                  )}`}
                />
              </Space>
            ))}
          </Space>
        )}
      </div>
      <div style={{ minWidth: 0, maxWidth: TEXT_WIDTH }}>
        <Text strong>{record.title}</Text>
        {authors && (
          <div>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              <EditOutlined style={{ marginRight: 4 }} />
              {authors}
            </Text>
          </div>
        )}
        {lectors && (
          <div>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              <SoundOutlined style={{ marginRight: 4 }} />
              {lectors}
            </Text>
          </div>
        )}
        {/* No top margin: with neither tag the row collapses to nothing. */}
        <Flex gap={token.marginXS} align="center" wrap>
          {/* Renders nothing unless exclusive now or a window is planned. */}
          <ExclusivityTag
            isExclusive={record.is_exclusive}
            ranges={record.exclusive_ranges}
          />
          {showPremiereTag && record.is_premiere && (
            <Tag color="magenta" style={{ marginInlineEnd: 0 }}>
              {t('base.labels.premiere')}
            </Tag>
          )}
        </Flex>
      </div>
    </Flex>
  );
};
